/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import {
  fetchCurrentTenantPlan,
  invalidateTenantPlanCache,
  isPlanExpired,
} from "../../admin_store/utils/subscriptionPlan";
import {
  getAuthRedirectPath,
  getTenantUsersFromAuthData,
  isAuthSubscriptionExpired,
  persistTenantAuth,
} from "./onboardingSession";

export async function submitUserLogin({
  identifier,
  password,
  isArabic,
  authLogin,
  fetchStoreData,
}) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(
    /\/+$/,
    ""
  );

  const res = await fetch(`${baseUrl}/Login/UserLogin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email: identifier,
      password,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || data?.Message || `HTTP ${res.status}`);

  const loginMessage = data?.message || "";
  const isEmployeeLogin = loginMessage.includes("Employee login successful");
  const isTenantLogin =
    loginMessage.includes("TenantUser login successful") ||
    loginMessage.includes("Tenant login successful");

  if (isEmployeeLogin) {
    const user = data?.employee;
    if (!user) throw new Error(isArabic ? "فشل تحميل المستخدم" : "Failed to load user");

    const userIsActive = user?.isActive === 1 || user?.IsActive === 1 || user?.isActive === true;
    if (!userIsActive) throw new Error(isArabic ? "الحساب غير نشط" : "Account is inactive");

    authLogin({
      token: data.token,
      user,
      authRole: "superAdmin",
      isTenantOwner: false,
      permissions: user,
    });

    localStorage.setItem("fullLoginResponse", JSON.stringify(data));
    localStorage.setItem("token", data?.token || "");
    localStorage.setItem("authToken", data?.token || "");
    localStorage.setItem("loginMessage", data?.message || "");

    return {
      authRole: "superAdmin",
      isTenantOwner: false,
      nextPath: "/admin",
      tenantPlanExpired: false,
      notification: "",
      subscription: null,
    };
  }

  if (!isTenantLogin) {
    throw new Error(isArabic ? "فشل تحميل المستخدم" : "Failed to load user");
  }

  const loginId = String(identifier || "").toLowerCase().trim();
  const usersList = getTenantUsersFromAuthData(data);
  const matchedUser =
    usersList.find((u) => String(u?.email || u?.Email || "").toLowerCase().trim() === loginId) ||
    usersList[0] ||
    data?.tenantUser ||
    data?.tenant;

  if (!matchedUser) throw new Error(isArabic ? "فشل تحميل المستخدم" : "Failed to load user");

  const userIsActive =
    matchedUser?.isActive === 1 || matchedUser?.IsActive === 1 || matchedUser?.isActive === true;
  if (!userIsActive) throw new Error(isArabic ? "الحساب غير نشط" : "Account is inactive");

  const persisted = persistTenantAuth(data, authLogin, {
    source: "login",
    identifier,
  });

  if (!persisted) {
    throw new Error(isArabic ? "فشل تحميل المستخدم" : "Failed to load user");
  }

  const tenantId =
    persisted.user?.tenantId ||
    matchedUser?.tenantId ||
    data?.tenant?.tenantId ||
    data?.tenantDetails?.tenantId ||
    persisted.tenant?.tenantId ||
    localStorage.getItem("tenantId");

  if (!tenantId) {
    throw new Error(isArabic ? "لم يتم تحديد هوية التاجر" : "Tenant ID not resolved");
  }

  if (typeof fetchStoreData === "function") {
    try {
      await fetchStoreData(tenantId);
    } catch (err) {
      console.warn("Could not load store data after login:", err);
    }
  }

  const hasSubscriptionFlag =
    data.subscriptionExpired !== undefined && data.subscriptionExpired !== null;
  const hasSubscriptionExpiry =
    data?.subscription &&
    data.subscription.isExpired !== undefined &&
    data.subscription.isExpired !== null;

  let tenantPlanExpired = isAuthSubscriptionExpired(data);

  if (!hasSubscriptionFlag && !hasSubscriptionExpiry) {
    try {
      invalidateTenantPlanCache();
      const tenantPlan = await fetchCurrentTenantPlan({ force: true });
      tenantPlanExpired = Boolean(tenantPlan && isPlanExpired(tenantPlan));
      localStorage.setItem("subscriptionExpired", tenantPlanExpired ? "true" : "false");
    } catch (err) {
      console.warn("Could not verify tenant plan from GetTenantPlan:", err);
    }
  }

  const nextPath = tenantPlanExpired
    ? "/theme-plan"
    : getAuthRedirectPath(data, { authRole: "tenant" });

  return {
    authRole: "tenant",
    isTenantOwner: persisted.isTenantOwner,
    nextPath,
    tenantPlanExpired,
    notification: persisted.notification || "",
    subscription: persisted.subscription || data?.subscription || null,
  };
}
